"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var xml2js_1 = require("xml2js");
var _isString = require('lodash/isString');
var _isNumber = require('lodash/isNumber');
var _get = require('lodash/get');
var _set = require('lodash/set');
/**
 * This parser can be used with the GeoStyler.
 * It implements the GeoStyler-Style StyleParser interface.
 *
 * @class SldStyleParser
 * @implements StyleParser
 */
var SldStyleParser = /** @class */ (function () {
    function SldStyleParser() {
        /**
         * Array of field / property names in a filter, which are casted to numerics
         * while parsing an SLD.
         */
        this._numericFilterFields = [];
        /**
         * Array of field / property names in a filter, which are casted to boolean
         * while parsing an SLD.
         */
        this._boolFilterFields = [];
        /**
         * Create a template string from a TextSymbolizer Label element.
         * Due to the non-bidirectional behaviour of xml2js, we cannot
         * recreate any template configuration. The current behaviour is as follows:
         *
         * Literals and Placeholders will be merge alternating, beginning with the property
         * that comes first. If the number of properties between Literals and Placeholders
         * is not equal, the remaining ones will be appended to the end of the template string.
         *
         * Examples:
         * <Label>
         *  <Literal>foo</Literal>
         *  <PropertyName>bar</PropertyName>
         * </Label>
         * --> "foo{{bar}}"
         *
         * <Label>
         *  <PropertyName>bar</PropertyName>
         *  <Literal>foo</Literal>
         * </Label>
         * --> "{{bar}}foo"
         *
         * <Label>
         *  <PropertyName>bar</PropertyName>
         *  <Literal>foo</Literal>
         *  <PropertyName>john</PropertyName>
         * </Label>
         * --> "{{bar}}foo{{john}}"
         *
         * <Label>
         *  <PropertyName>bar</PropertyName>
         *  <PropertyName>john</PropertyName>
         *  <Literal>foo</Literal>
         * </Label>
         * --> "{{bar}}foo{{john}}"
         *
         * <Label>
         *  <PropertyName>bar</PropertyName>
         *  <PropertyName>john</PropertyName>
         *  <PropertyName>doe</PropertyName>
         *  <Literal>foo</Literal>
         * </Label>
         * --> "{{bar}}foo{{john}}{{doe}}"
         *
         */
        this.getTextSymbolizerLabelFromSldSymbolizer = function (sldLabel) {
            var label = '';
            var literals = _get(sldLabel, 'Literal');
            var placeholders = _get(sldLabel, 'PropertyName');
            var literalIsFirst = Object.keys(sldLabel)[0] === 'Literal';
            if (placeholders && placeholders.length > 0) {
                // if placeholders are being used
                // add braces around placeholders
                var placeholdersBraces_1 = placeholders.map(function (plc) { return "{{" + plc + "}}"; });
                if (literals && literals.length > 0) {
                    // if there are placeholders and literals
                    if (literalIsFirst) {
                        // start with literals
                        literals.forEach(function (lit, idx) {
                            label += "" + lit;
                            if (placeholdersBraces_1[idx]) {
                                label += "" + placeholdersBraces_1[idx];
                            }
                        });
                        // if there are more placeholders than literals,
                        // add the remaining placeholders at the end
                        if (placeholdersBraces_1.length > literals.length) {
                            label += placeholdersBraces_1.join('');
                        }
                    }
                    else {
                        // start with placeholders
                        placeholdersBraces_1.forEach(function (plc, idx) {
                            label += "" + plc;
                            if (literals[idx]) {
                                label += "" + literals[idx];
                            }
                        });
                        // if there are more literals than placeholders,
                        // add the remaining literals at the end
                        if (literals.length > placeholdersBraces_1.length) {
                            label += literals.join('');
                        }
                    }
                }
                else {
                    // if there are placeholders but no literals
                    // set curly braces around placeholders and simply join them with no spaces
                    label = placeholdersBraces_1.join('');
                }
            }
            else if (literals && literals.length > 0) {
                // if no placeholders are being used
                // create a simple string
                label = literals.join('');
            }
            return label;
        };
        /**
         * Get the Label from a TextSymbolizer
         */
        this.getSldLabelFromTextSymbolizer = function (template) {
            // prefix indicating that a template is being used
            var prefix = '\\{\\{';
            // suffix indicating that a template is being used
            var suffix = '\\}\\}';
            // RegExp to match all occurences encapsuled between two curly braces
            // including the curly braces
            var regExp = new RegExp(prefix + '.*?' + suffix, 'g');
            var regExpRes = template.match(regExp);
            // check if a template starts with a placeholder or a literal
            var startsWithPlaceholder = template.startsWith('{{');
            // if no template was used, return as fix string
            if (!regExpRes) {
                return [
                    {
                        'ogc:Literal': [template]
                    }
                ];
                // if templates are being used
            }
            else {
                // split the original string at occurences of placeholders
                // the resulting array will be used for the Literal property
                var literalsWEmptyStrings = template.split(regExp);
                var literals_1 = [];
                // remove empty strings
                literalsWEmptyStrings.forEach(function (lit) {
                    if (lit.length !== 0) {
                        literals_1.push(lit);
                    }
                });
                // slice the curly braces of the placeholder matches
                // and use the resulting array for the PropertyName property
                var propertyName = regExpRes.map(function (reg) {
                    return reg.slice(2, reg.length - 2);
                });
                // if template starts with a placeholder, PropertyName must be set first
                // otherwise Literal must be set first.
                if (startsWithPlaceholder) {
                    return [{
                            'ogc:PropertyName': propertyName,
                            'ogc:Literal': literals_1
                        }];
                }
                else {
                    return [{
                            'ogc:Literal': literals_1,
                            'ogc:PropertyName': propertyName
                        }];
                }
            }
        };
    }
    Object.defineProperty(SldStyleParser.prototype, "numericFilterFields", {
        /**
         * Getter for _numericFilterFields
         * @return {string[]} The numericFilterFields
         */
        get: function () {
            return this._numericFilterFields;
        },
        /**
         * Setter for _numericFilterFields
         * @param {string[]} numericFilterFields The numericFilterFields to set
         */
        set: function (numericFilterFields) {
            this._numericFilterFields = numericFilterFields;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(SldStyleParser.prototype, "boolFilterFields", {
        /**
         * Getter for _boolFilterFields
         * @return {string[]} The boolFilterFields
         */
        get: function () {
            return this._boolFilterFields;
        },
        /**
         * Setter for _boolFilterFields
         * @param {string[]} boolFilterFields The boolFilterFields to set
         */
        set: function (boolFilterFields) {
            this._boolFilterFields = boolFilterFields;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns the keys of an object where the value is equal to the passed in
     * value.
     *
     * @param {object} object The object to get the key from.
     * @param {any} value The value to get the matching key from.
     * @return {string[]} The matching keys.
     */
    SldStyleParser.keysByValue = function (object, value) {
        return Object.keys(object).filter(function (key) { return object[key] === value; });
    };
    /**
     * The name Processor is passed as an option to the xml2js parser and modifies
     * the tagName. It strips all namespaces from the tags.
     *
     * @param {string} name The originial tagName
     * @return {string} The modified tagName
     */
    SldStyleParser.prototype.tagNameProcessor = function (name) {
        var prefixMatch = new RegExp(/(?!xmlns)^.*:/);
        return name.replace(prefixMatch, '');
    };
    /**
     * Get the name for the Style from the SLD Object. Returns the Title of the UserStyle
     * if defined or the Name of the NamedLayer if defined or an empty string.
     *
     * @param {object} sldObject The SLD object representation (created with xml2js)
     * @return {string} The name to be used for the GeoStyler Style Style
     */
    SldStyleParser.prototype.getStyleNameFromSldObject = function (sldObject) {
        var userStyleTitle = _get(sldObject, 'StyledLayerDescriptor.NamedLayer[0].UserStyle[0].Title[0]');
        var namedLayerName = _get(sldObject, 'StyledLayerDescriptor.NamedLayer[0].Name[0]');
        return userStyleTitle ? userStyleTitle
            : namedLayerName ? namedLayerName : '';
    };
    /**
     * Creates a GeoStyler-Style Filter from a given operator name and the js
     * SLD object representation (created with xml2js) of the SLD Filter.
     *
     * @param {string} sldOperatorName The Name of the SLD Filter Operator
     * @param {object} sldFilter The SLD Filter
     * @return {Filter} The GeoStyler-Style Filter
     */
    SldStyleParser.prototype.getFilterFromOperatorAndComparison = function (sldOperatorName, sldFilter) {
        var _this = this;
        var filter;
        if (Object.keys(SldStyleParser.comparisonMap).includes(sldOperatorName)) {
            var comparisonOperator = SldStyleParser.comparisonMap[sldOperatorName];
            var property = sldFilter.PropertyName[0];
            var value = null;
            if (sldOperatorName !== 'PropertyIsNull') {
                value = sldFilter.Literal[0];
            }
            if (this.numericFilterFields.indexOf(property) !== -1 && !Number.isNaN(parseFloat(value))) {
                value = parseFloat(value);
            }
            if (_isString(value)) {
                var lowerValue = value.toLowerCase();
                if (this.boolFilterFields.indexOf(property) !== -1) {
                    if (lowerValue === 'false') {
                        value = false;
                    }
                    if (lowerValue === 'true') {
                        value = true;
                    }
                }
            }
            filter = [
                comparisonOperator,
                property,
                value
            ];
        }
        else if (Object.keys(SldStyleParser.combinationMap).includes(sldOperatorName)) {
            var combinationOperator = SldStyleParser.combinationMap[sldOperatorName];
            var filters_1 = [];
            Object.keys(sldFilter).forEach(function (op) {
                if (sldFilter[op].length === 1) {
                    filters_1.push(_this.getFilterFromOperatorAndComparison(op, sldFilter[op][0]));
                }
                else {
                    sldFilter[op].forEach(function (el) {
                        filters_1.push(_this.getFilterFromOperatorAndComparison(op, el));
                    });
                }
            });
            filter = [
                combinationOperator
            ].concat(filters_1);
        }
        else if (Object.keys(SldStyleParser.negationOperatorMap).includes(sldOperatorName)) {
            var negationOperator = SldStyleParser.negationOperatorMap[sldOperatorName];
            var negatedOperator = Object.keys(sldFilter)[0];
            var negatedComparison = sldFilter[negatedOperator][0];
            var negatedFilter = this.getFilterFromOperatorAndComparison(negatedOperator, negatedComparison);
            filter = [
                negationOperator,
                negatedFilter
            ];
        }
        else {
            throw new Error('No Filter detected');
        }
        return filter;
    };
    /**
     * Get the GeoStyler-Style Filter from an SLD Rule.
     *
     * Currently only supports one Filter per Rule.
     *
     * @param {object} sldRule The SLD Rule
     * @return {Filter} The GeoStyler-Style Filter
     */
    SldStyleParser.prototype.getFilterFromRule = function (sldRule) {
        var sldFilters = sldRule.Filter;
        if (!sldFilters) {
            return;
        }
        var sldFilter = sldFilters[0];
        var operator = Object.keys(sldFilter).find(function (key, index) {
            return key !== '$';
        });
        if (!operator) {
            return;
        }
        var comparison = sldFilter[operator][0];
        var filter = this.getFilterFromOperatorAndComparison(operator, comparison);
        return filter;
    };
    /**
     * Get the GeoStyler-Style ScaleDenominator from an SLD Rule.
     *
     * @param {object} sldRule The SLD Rule
     * @return {ScaleDenominator} The GeoStyler-Style ScaleDenominator
     */
    SldStyleParser.prototype.getScaleDenominatorFromRule = function (sldRule) {
        var scaleDenominator = {};
        if (sldRule.MinScaleDenominator) {
            scaleDenominator.min = parseFloat(sldRule.MinScaleDenominator[0]);
        }
        if (sldRule.MaxScaleDenominator) {
            scaleDenominator.max = parseFloat(sldRule.MaxScaleDenominator[0]);
        }
        return (scaleDenominator.min || scaleDenominator.max)
            ? scaleDenominator
            : undefined;
    };
    /**
     * Get the GeoStyler-Style MarkSymbolizer from an SLD Symbolizer
     *
     * @param {object} sldSymbolizer The SLD Symbolizer
     * @return {MarkSymbolizer} The GeoStyler-Style MarkSymbolizer
     */
    SldStyleParser.prototype.getMarkSymbolizerFromSldSymbolizer = function (sldSymbolizer) {
        var wellKnownName = _get(sldSymbolizer, 'Graphic[0].Mark[0].WellKnownName[0]');
        var strokeParams = _get(sldSymbolizer, 'Graphic[0].Mark[0].Stroke[0].CssParameter') || [];
        var opacity = _get(sldSymbolizer, 'Graphic[0].Opacity[0]');
        var size = _get(sldSymbolizer, 'Graphic[0].Size[0]');
        var rotation = _get(sldSymbolizer, 'Graphic[0].Rotation[0]');
        var fillParams = _get(sldSymbolizer, 'Graphic[0].Mark[0].Fill[0].CssParameter') || [];
        var colorIdx = fillParams.findIndex(function (cssParam) {
            return cssParam.$.name === 'fill';
        });
        var color = _get(sldSymbolizer, 'Graphic[0].Mark[0].Fill[0].CssParameter[' + colorIdx + ']._');
        var markSymbolizer = {
            kind: 'Mark',
        };
        if (opacity) {
            markSymbolizer.opacity = parseFloat(opacity);
        }
        if (color) {
            markSymbolizer.color = color;
        }
        if (rotation) {
            markSymbolizer.rotate = parseFloat(rotation);
        }
        if (size) {
            markSymbolizer.radius = parseFloat(size) / 2;
        }
        switch (wellKnownName) {
            case 'circle':
            case 'square':
            case 'triangle':
            case 'star':
            case 'cross':
            case 'x':
                var wkn = wellKnownName.charAt(0).toUpperCase() + wellKnownName.slice(1);
                markSymbolizer.wellKnownName = wkn;
                break;
            case 'shape://vertline':
            case 'shape://horline':
            case 'shape://slash':
            case 'shape://backslash':
            case 'shape://dot':
            case 'shape://plus':
            case 'shape://times':
            case 'shape://oarrow':
            case 'shape://carrow':
                markSymbolizer.wellKnownName = wellKnownName;
                break;
            default:
                throw new Error("MarkSymbolizer cannot be parsed. Unsupported WellKnownName.");
        }
        strokeParams.forEach(function (param) {
            switch (param.$.name) {
                case 'stroke':
                    markSymbolizer.strokeColor = param._;
                    break;
                case 'stroke-width':
                    markSymbolizer.strokeWidth = parseFloat(param._);
                    break;
                case 'stroke-opacity':
                    markSymbolizer.strokeOpacity = parseFloat(param._);
                    break;
                default:
                    break;
            }
        });
        return markSymbolizer;
    };
    /**
     * Get the GeoStyler-Style IconSymbolizer from an SLD Symbolizer
     *
     * @param {object} sldSymbolizer The SLD Symbolizer
     * @return {IconSymbolizer} The GeoStyler-Style IconSymbolizer
     */
    SldStyleParser.prototype.getIconSymbolizerFromSldSymbolizer = function (sldSymbolizer) {
        var onlineResource = _get(sldSymbolizer, 'Graphic[0].ExternalGraphic[0].OnlineResource[0]');
        var iconSymbolizer = {
            kind: 'Icon',
            image: onlineResource.$['xlink:href']
        };
        var opacity = _get(sldSymbolizer, 'Graphic[0].Opacity[0]');
        var size = _get(sldSymbolizer, 'Graphic[0].Size[0]');
        var rotate = _get(sldSymbolizer, 'Graphic[0].Rotation[0]');
        if (opacity) {
            iconSymbolizer.opacity = opacity;
        }
        if (size) {
            iconSymbolizer.size = parseInt(size, 10);
        }
        if (rotate) {
            iconSymbolizer.rotate = parseInt(rotate, 10);
        }
        return iconSymbolizer;
    };
    /**
     * Get the GeoStyler-Style PointSymbolizer from an SLD Symbolizer.
     *
     * The opacity of the Symbolizer is taken from the <Graphic>.
     *
     * @param {object} sldSymbolizer The SLD Symbolizer
     * @return {PointSymbolizer} The GeoStyler-Style PointSymbolizer
     */
    SldStyleParser.prototype.getPointSymbolizerFromSldSymbolizer = function (sldSymbolizer) {
        var pointSymbolizer = {};
        var wellKnownName = _get(sldSymbolizer, 'Graphic[0].Mark[0].WellKnownName[0]');
        var externalGraphic = _get(sldSymbolizer, 'Graphic[0].ExternalGraphic[0]');
        if (externalGraphic) {
            pointSymbolizer = this.getIconSymbolizerFromSldSymbolizer(sldSymbolizer);
        }
        else {
            // geoserver does not set a wellKnownName for square explicitly since it is the default value.
            // Therefore, we have to set the wellKnownName to square if no wellKownName is given.
            if (!wellKnownName) {
                _set(sldSymbolizer, 'Graphic[0].Mark[0].WellKnownName[0]', 'square');
            }
            pointSymbolizer = this.getMarkSymbolizerFromSldSymbolizer(sldSymbolizer);
        }
        return pointSymbolizer;
    };
    /**
     * Get the GeoStyler-Style LineSymbolizer from an SLD Symbolizer.
     *
     * Currently only the CssParameters are available.
     *
     * @param {object} sldSymbolizer The SLD Symbolizer
     * @return {LineSymbolizer} The GeoStyler-Style LineSymbolizer
     */
    SldStyleParser.prototype.getLineSymbolizerFromSldSymbolizer = function (sldSymbolizer) {
        var _this = this;
        var lineSymbolizer = {
            kind: 'Line'
        };
        var strokeKeys = Object.keys(_get(sldSymbolizer, 'Stroke[0]')) || [];
        if (strokeKeys.length < 1) {
            throw new Error("LineSymbolizer cannot be parsed. No Stroke detected");
        }
        strokeKeys.forEach(function (strokeKey) {
            switch (strokeKey) {
                case 'CssParameter':
                    var cssParameters = _get(sldSymbolizer, 'Stroke[0].CssParameter') || [];
                    if (cssParameters.length < 1) {
                        throw new Error("LineSymbolizer can not be parsed. No CssParameters detected.");
                    }
                    cssParameters.forEach(function (cssParameter) {
                        var name = cssParameter.$.name, value = cssParameter._;
                        switch (name) {
                            case 'stroke':
                                lineSymbolizer.color = value;
                                break;
                            case 'stroke-width':
                                lineSymbolizer.width = parseFloat(value);
                                break;
                            case 'stroke-opacity':
                                lineSymbolizer.opacity = parseFloat(value);
                                break;
                            case 'stroke-linejoin':
                                // geostyler-style and ol use 'miter' whereas sld uses 'mitre'
                                if (value === 'mitre') {
                                    lineSymbolizer.join = 'miter';
                                }
                                else {
                                    lineSymbolizer.join = value;
                                }
                                break;
                            case 'stroke-linecap':
                                lineSymbolizer.cap = value;
                                break;
                            case 'stroke-dasharray':
                                var dashStringAsArray = value.split(' ').map(function (a) { return parseFloat(a); });
                                lineSymbolizer.dasharray = dashStringAsArray;
                                break;
                            case 'stroke-dashoffset':
                                lineSymbolizer.dashOffset = parseFloat(value);
                                break;
                            default:
                                break;
                        }
                    });
                    break;
                case 'GraphicStroke':
                    lineSymbolizer.graphicStroke = _this.getPointSymbolizerFromSldSymbolizer(sldSymbolizer.Stroke[0].GraphicStroke[0]);
                    break;
                case 'GraphicFill':
                    lineSymbolizer.graphicFill = _this.getPointSymbolizerFromSldSymbolizer(sldSymbolizer.Stroke[0].GraphicFill[0]);
                    break;
                default:
                    break;
            }
        });
        var perpendicularOffset = _get(sldSymbolizer, 'PerpendicularOffset[0]');
        if (perpendicularOffset !== undefined) {
            lineSymbolizer.perpendicularOffset = Number(perpendicularOffset);
        }
        return lineSymbolizer;
    };
    /**
     * Get the GeoStyler-Style FillSymbolizer from an SLD Symbolizer.
     *
     * PolygonSymbolizer Stroke is just partially supported.
     *
     * @param {object} sldSymbolizer The SLD Symbolizer
     * @return {FillSymbolizer} The GeoStyler-Style FillSymbolizer
     */
    SldStyleParser.prototype.getFillSymbolizerFromSldSymbolizer = function (sldSymbolizer) {
        var fillSymbolizer = {
            kind: 'Fill'
        };
        var fillCssParameters = _get(sldSymbolizer, 'Fill[0].CssParameter') || [];
        var strokeCssParameters = _get(sldSymbolizer, 'Stroke[0].CssParameter') || [];
        var graphicFill = _get(sldSymbolizer, 'Fill[0].GraphicFill[0]');
        if (graphicFill) {
            fillSymbolizer.graphicFill = this.getPointSymbolizerFromSldSymbolizer(graphicFill);
        }
        fillCssParameters.forEach(function (cssParameter) {
            var name = cssParameter.$.name, value = cssParameter._;
            switch (name) {
                case 'fill':
                    fillSymbolizer.color = value;
                    break;
                case 'fill-opacity':
                    fillSymbolizer.opacity = parseFloat(value);
                    break;
                default:
                    break;
            }
        });
        strokeCssParameters.forEach(function (cssParameter) {
            var name = cssParameter.$.name, value = cssParameter._;
            if (name === 'stroke') {
                fillSymbolizer.outlineColor = value;
            }
            else if (name === 'stroke-width') {
                fillSymbolizer.outlineWidth = parseInt(value, 10);
            }
            else if (name === 'stroke-dasharray') {
                var outlineDasharrayStr = value.split(' ');
                var outlineDasharray_1 = [];
                outlineDasharrayStr.forEach(function (dashStr) {
                    outlineDasharray_1.push(parseInt(dashStr, 10));
                });
                fillSymbolizer.outlineDasharray = outlineDasharray_1;
            }
        });
        return fillSymbolizer;
    };
    /**
     * Get the GeoStyler-Style TextSymbolizer from an SLD Symbolizer.
     *
     * @param {object} sldSymbolizer The SLD Symbolizer
     * @return {TextSymbolizer} The GeoStyler-Style TextSymbolizer
     */
    SldStyleParser.prototype.getTextSymbolizerFromSldSymbolizer = function (sldSymbolizer) {
        var textSymbolizer = {
            kind: 'Text'
        };
        var fontCssParameters = _get(sldSymbolizer, 'Font[0].CssParameter') || [];
        var label = _get(sldSymbolizer, 'Label[0]');
        if (label) {
            textSymbolizer.label = this.getTextSymbolizerLabelFromSldSymbolizer(label);
        }
        var color = _get(sldSymbolizer, 'Fill[0].CssParameter[0]._');
        var haloColorCssParameter = _get(sldSymbolizer, 'Halo[0].Fill[0].CssParameter') || [];
        var haloRadius = _get(sldSymbolizer, 'Halo[0].Radius[0]');
        if (color) {
            textSymbolizer.color = color;
        }
        if (haloRadius) {
            textSymbolizer.haloWidth = parseFloat(haloRadius);
        }
        haloColorCssParameter.forEach(function (cssParameter) {
            var name = cssParameter.$.name, value = cssParameter._;
            switch (name) {
                case 'fill':
                    textSymbolizer.haloColor = value;
                    break;
                case 'fill-opacity':
                default:
                    break;
            }
        });
        var displacement = _get(sldSymbolizer, 'LabelPlacement[0].PointPlacement[0].Displacement[0]');
        if (displacement) {
            var x = displacement.DisplacementX[0];
            var y = displacement.DisplacementY[0];
            textSymbolizer.offset = [
                x ? parseFloat(x) : 0,
                y ? parseFloat(y) : 0,
            ];
        }
        var rotation = _get(sldSymbolizer, 'LabelPlacement[0].PointPlacement[0].Rotation[0]');
        if (rotation) {
            textSymbolizer.rotate = parseFloat(rotation);
        }
        fontCssParameters.forEach(function (cssParameter) {
            var name = cssParameter.$.name, value = cssParameter._;
            switch (name) {
                case 'font-family':
                    textSymbolizer.font = [value];
                    break;
                case 'font-style':
                    // Currently not supported by GeoStyler Style
                    break;
                case 'font-weight':
                    // Currently not supported by GeoStyler Style
                    break;
                case 'font-size':
                    textSymbolizer.size = parseFloat(value);
                    break;
                default:
                    break;
            }
        });
        return textSymbolizer;
    };
    /**
     * Get the GeoStyler-Style Symbolizers from an SLD Rule.
     *
     * @param {object} sldRule The SLD Rule
     * @return {Symbolizer[]} The GeoStyler-Style Symbolizer Array
     */
    SldStyleParser.prototype.getSymbolizersFromRule = function (sldRule) {
        var _this = this;
        var symbolizers = [];
        var symbolizerNames = Object.keys(sldRule).filter(function (key) { return key.endsWith('Symbolizer'); });
        symbolizerNames.forEach(function (sldSymbolizerName) {
            sldRule[sldSymbolizerName].forEach(function (sldSymbolizer) {
                var symbolizer;
                switch (sldSymbolizerName) {
                    case 'PointSymbolizer':
                        symbolizer = _this.getPointSymbolizerFromSldSymbolizer(sldSymbolizer);
                        break;
                    case 'LineSymbolizer':
                        symbolizer = _this.getLineSymbolizerFromSldSymbolizer(sldSymbolizer);
                        break;
                    case 'TextSymbolizer':
                        symbolizer = _this.getTextSymbolizerFromSldSymbolizer(sldSymbolizer);
                        break;
                    case 'PolygonSymbolizer':
                        symbolizer = _this.getFillSymbolizerFromSldSymbolizer(sldSymbolizer);
                        break;
                    default:
                        throw new Error('Failed to parse SymbolizerKind from SldRule');
                }
                symbolizers.push(symbolizer);
            });
        });
        return symbolizers;
    };
    /**
     * Get the GeoStyler-Style Rule from an SLD Object (created with xml2js).
     *
     * @param {object} sldObject The SLD object representation (created with xml2js)
     * @return {Rule} The GeoStyler-Style Rule
     */
    SldStyleParser.prototype.getRulesFromSldObject = function (sldObject) {
        var _this = this;
        var layers = sldObject.StyledLayerDescriptor.NamedLayer;
        var rules = [];
        layers.forEach(function (layer) {
            layer.UserStyle.forEach(function (userStyle) {
                userStyle.FeatureTypeStyle.forEach(function (featureTypeStyle) {
                    featureTypeStyle.Rule.forEach(function (sldRule) {
                        var filter = _this.getFilterFromRule(sldRule);
                        var scaleDenominator = _this.getScaleDenominatorFromRule(sldRule);
                        var symbolizers = _this.getSymbolizersFromRule(sldRule);
                        var name = sldRule.Title ? sldRule.Title[0]
                            : (sldRule.Name ? sldRule.Name[0] : '');
                        var rule = {
                            name: name
                        };
                        if (filter) {
                            rule.filter = filter;
                        }
                        if (scaleDenominator) {
                            rule.scaleDenominator = scaleDenominator;
                        }
                        if (symbolizers) {
                            rule.symbolizers = symbolizers;
                        }
                        rules.push(rule);
                    });
                });
            });
        });
        return rules;
    };
    /**
     * Get the GeoStyler-Style Style from an SLD Object (created with xml2js).
     *
     * @param {object} sldObject The SLD object representation (created with xml2js)
     * @return {Style} The GeoStyler-Style Style
     */
    SldStyleParser.prototype.sldObjectToGeoStylerStyle = function (sldObject) {
        var rules = this.getRulesFromSldObject(sldObject);
        var name = this.getStyleNameFromSldObject(sldObject);
        return {
            name: name,
            rules: rules
        };
    };
    /**
     * The readStyle implementation of the GeoStyler-Style StyleParser interface.
     * It reads a SLD as a string and returns a Promise.
     * The Promise itself resolves with a GeoStyler-Style Style.
     *
     * @param {string} sldString A SLD as a string.
     * @return {Promise} The Promise resolving with the GeoStyler-Style Style
     */
    SldStyleParser.prototype.readStyle = function (sldString) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var options = {
                tagNameProcessors: [_this.tagNameProcessor]
            };
            try {
                xml2js_1.parseString(sldString, options, function (err, result) {
                    if (err) {
                        reject("Error while parsing sldString: " + err);
                    }
                    var geoStylerStyle = _this.sldObjectToGeoStylerStyle(result);
                    resolve(geoStylerStyle);
                });
            }
            catch (error) {
                reject(error);
            }
        });
    };
    /**
     * The writeStyle implementation of the GeoStyler-Style StyleParser interface.
     * It reads a GeoStyler-Style Style and returns a Promise.
     * The Promise itself resolves with a SLD string.
     *
     * @param {Style} geoStylerStyle A GeoStyler-Style Style.
     * @return {Promise} The Promise resolving with the SLD as a string.
     */
    SldStyleParser.prototype.writeStyle = function (geoStylerStyle) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            try {
                var builder = new xml2js_1.Builder();
                var sldObject = _this.geoStylerStyleToSldObject(geoStylerStyle);
                var sldString = builder.buildObject(sldObject);
                resolve(sldString);
            }
            catch (error) {
                reject(error);
            }
        });
    };
    /**
     * Get the SLD Object (readable with xml2js) from an GeoStyler-Style Style
     *
     * @param {Style} geoStylerStyle A GeoStyler-Style Style.
     * @return {object} The object representation of a SLD Style (readable with xml2js)
     */
    SldStyleParser.prototype.geoStylerStyleToSldObject = function (geoStylerStyle) {
        var rules = this.getSldRulesFromRules(geoStylerStyle.rules);
        // add the ogc namespace to the filter element, if a filter is present
        rules.forEach(function (rule) {
            if (rule.Filter && !rule.Filter.$) {
                rule.Filter.$ = { 'xmlns': 'http://www.opengis.net/ogc' };
            }
        });
        return {
            StyledLayerDescriptor: {
                '$': {
                    'version': '1.0.0',
                    'xsi:schemaLocation': 'http://www.opengis.net/sld StyledLayerDescriptor.xsd',
                    'xmlns': 'http://www.opengis.net/sld',
                    'xmlns:ogc': 'http://www.opengis.net/ogc',
                    'xmlns:xlink': 'http://www.w3.org/1999/xlink',
                    'xmlns:xsi': 'http://www.w3.org/2001/XMLSchema-instance'
                },
                'NamedLayer': [{
                        'Name': [geoStylerStyle.name],
                        'UserStyle': [{
                                'Name': [geoStylerStyle.name],
                                'Title': [geoStylerStyle.name],
                                'FeatureTypeStyle': [{
                                        'Rule': rules
                                    }]
                            }]
                    }]
            }
        };
    };
    /**
     * Get the SLD Object (readable with xml2js) from an GeoStyler-Style Rule.
     *
     * @param {Rule[]} rules An array of GeoStyler-Style Rules.
     * @return {object} The object representation of a SLD Rule (readable with xml2js)
     */
    SldStyleParser.prototype.getSldRulesFromRules = function (rules) {
        var _this = this;
        return rules.map(function (rule) {
            var sldRule = {
                Name: [rule.name]
            };
            if (rule.filter) {
                var filter = _this.getSldFilterFromFilter(rule.filter);
                sldRule.Filter = filter;
            }
            if (rule.scaleDenominator) {
                var _a = rule.scaleDenominator, min = _a.min, max = _a.max;
                if (min && _isNumber(min)) {
                    sldRule.MinScaleDenominator = [min.toString()];
                }
                if (max && _isNumber(max)) {
                    sldRule.MaxScaleDenominator = [max.toString()];
                }
            }
            // Remove empty Symbolizers and check if there is at least 1 symbolizer
            var symbolizers = _this.getSldSymbolizersFromSymbolizers(rule.symbolizers);
            var symbolizerKeys = Object.keys(symbolizers[0]);
            symbolizerKeys.forEach(function (key) {
                if (symbolizers[0][key].length === 0) {
                    delete symbolizers[0][key];
                }
            });
            if (Object.keys(symbolizers[0]).length !== 0) {
                sldRule = Object.assign(sldRule, symbolizers[0]);
            }
            return sldRule;
        });
    };
    /**
     * Get the SLD Object (readable with xml2js) from GeoStyler-Style Symbolizers.
     *
     * @param {Symbolizer[]} symbolizers A GeoStyler-Style Symbolizer array.
     * @return {object} The object representation of a SLD Symbolizer (readable with xml2js)
     */
    SldStyleParser.prototype.getSldSymbolizersFromSymbolizers = function (symbolizers) {
        var _this = this;
        var sldSymbolizers = [];
        var sldSymbolizer = {};
        symbolizers.forEach(function (symb) {
            var sldSymb;
            switch (symb.kind) {
                case 'Mark':
                    if (!sldSymbolizer.PointSymbolizer) {
                        sldSymbolizer.PointSymbolizer = [];
                    }
                    sldSymb = _this.getSldPointSymbolizerFromMarkSymbolizer(symb);
                    if (_get(sldSymb, 'PointSymbolizer[0]')) {
                        sldSymbolizer.PointSymbolizer.push(_get(sldSymb, 'PointSymbolizer[0]'));
                    }
                    break;
                case 'Icon':
                    if (!sldSymbolizer.PointSymbolizer) {
                        sldSymbolizer.PointSymbolizer = [];
                    }
                    sldSymb = _this.getSldPointSymbolizerFromIconSymbolizer(symb);
                    if (_get(sldSymb, 'PointSymbolizer[0]')) {
                        sldSymbolizer.PointSymbolizer.push(_get(sldSymb, 'PointSymbolizer[0]'));
                    }
                    break;
                case 'Text':
                    if (!sldSymbolizer.TextSymbolizer) {
                        sldSymbolizer.TextSymbolizer = [];
                    }
                    sldSymb = _this.getSldTextSymbolizerFromTextSymbolizer(symb);
                    if (_get(sldSymb, 'TextSymbolizer[0]')) {
                        sldSymbolizer.TextSymbolizer.push(_get(sldSymb, 'TextSymbolizer[0]'));
                    }
                    break;
                case 'Line':
                    if (!sldSymbolizer.LineSymbolizer) {
                        sldSymbolizer.LineSymbolizer = [];
                    }
                    sldSymb = _this.getSldLineSymbolizerFromLineSymbolizer(symb);
                    if (_get(sldSymb, 'LineSymbolizer[0]')) {
                        sldSymbolizer.LineSymbolizer.push(_get(sldSymb, 'LineSymbolizer[0]'));
                    }
                    break;
                case 'Fill':
                    if (!sldSymbolizer.PolygonSymbolizer) {
                        sldSymbolizer.PolygonSymbolizer = [];
                    }
                    sldSymb = _this.getSldPolygonSymbolizerFromFillSymbolizer(symb);
                    if (_get(sldSymb, 'PolygonSymbolizer[0]')) {
                        sldSymbolizer.PolygonSymbolizer.push(_get(sldSymb, 'PolygonSymbolizer[0]'));
                    }
                    break;
                default:
                    break;
            }
            sldSymbolizers.push(sldSymbolizer);
        });
        return sldSymbolizers;
    };
    /**
     * Get the SLD Object (readable with xml2js) from an GeoStyler-Style TextSymbolizer.
     *
     * @param {TextSymbolizer} textSymbolizer A GeoStyler-Style TextSymbolizer.
     * @return {object} The object representation of a SLD TextSymbolizer (readable with xml2js)
     */
    SldStyleParser.prototype.getSldTextSymbolizerFromTextSymbolizer = function (textSymbolizer) {
        var sldTextSymbolizer = [{
                'Label': textSymbolizer.label ? this.getSldLabelFromTextSymbolizer(textSymbolizer.label) : undefined
            }];
        var fontPropertyMap = {
            font: 'font-family',
            size: 'font-size'
        };
        var fontCssParameters = Object.keys(textSymbolizer)
            .filter(function (property) { return property !== 'kind' && fontPropertyMap[property]; })
            .map(function (property) {
            return {
                '_': property === 'font'
                    ? textSymbolizer[property][0]
                    : textSymbolizer[property],
                '$': {
                    'name': fontPropertyMap[property]
                }
            };
        });
        if (fontCssParameters.length > 0) {
            sldTextSymbolizer[0].Font = [{
                    'CssParameter': fontCssParameters
                }];
        }
        if (textSymbolizer.offset || textSymbolizer.rotate !== undefined) {
            var pointPlacement = [{}];
            if (textSymbolizer.offset) {
                pointPlacement[0].Displacement = [{
                        'DisplacementX': [
                            textSymbolizer.offset[0].toString()
                        ],
                        'DisplacementY': [
                            textSymbolizer.offset[1].toString()
                        ]
                    }];
            }
            if (textSymbolizer.rotate !== undefined) {
                pointPlacement[0].Rotation = [textSymbolizer.rotate.toString()];
            }
            sldTextSymbolizer[0].LabelPlacement = [{
                    PointPlacement: pointPlacement
                }];
        }
        if (textSymbolizer.color) {
            sldTextSymbolizer[0].Fill = [{
                    'CssParameter': [{
                            '_': textSymbolizer.color,
                            '$': {
                                'name': 'fill'
                            }
                        }]
                }];
        }
        if (textSymbolizer.haloWidth || textSymbolizer.haloColor) {
            var halo = {};
            var haloCssParameter = [];
            if (textSymbolizer.haloWidth) {
                halo.Radius = [textSymbolizer.haloWidth.toString()];
            }
            if (textSymbolizer.haloColor) {
                haloCssParameter.push({
                    '_': textSymbolizer.haloColor,
                    '$': {
                        'name': 'fill'
                    }
                });
            }
            if (haloCssParameter.length > 0) {
                halo.Fill = [{
                        CssParameter: haloCssParameter
                    }];
            }
            sldTextSymbolizer[0].Halo = [halo];
        }
        return {
            'TextSymbolizer': sldTextSymbolizer
        };
    };
    /**
     * Get the SLD Object (readable with xml2js) from an GeoStyler-Style FillSymbolizer.
     *
     * @param {FillSymbolizer} fillSymbolizer A GeoStyler-Style FillSymbolizer.
     * @return {object} The object representation of a SLD PolygonSymbolizer (readable with xml2js)
     */
    SldStyleParser.prototype.getSldPolygonSymbolizerFromFillSymbolizer = function (fillSymbolizer) {
        var strokePropertyMap = {
            outlineColor: 'stroke',
            outlineWidth: 'stroke-width',
            outlineDasharray: 'stroke-dasharray'
        };
        var fillPropertyMap = {
            color: 'fill',
            opacity: 'fill-opacity'
        };
        var strokeCssParameters = [];
        var fillCssParameters = [];
        var graphicFill;
        if (_get(fillSymbolizer, 'graphicFill')) {
            if (_get(fillSymbolizer, 'graphicFill.kind') === 'Mark') {
                graphicFill = this.getSldPointSymbolizerFromMarkSymbolizer(fillSymbolizer.graphicFill);
            }
            else if (_get(fillSymbolizer, 'graphicFill.kind') === 'Icon') {
                graphicFill = this.getSldPointSymbolizerFromIconSymbolizer(fillSymbolizer.graphicFill);
            }
        }
        Object.keys(fillSymbolizer)
            .filter(function (property) { return property !== 'kind'; })
            .filter(function (property) { return fillSymbolizer[property]; })
            .forEach(function (property) {
            if (Object.keys(fillPropertyMap).includes(property)) {
                fillCssParameters.push({
                    '_': fillSymbolizer[property],
                    '$': {
                        'name': fillPropertyMap[property]
                    }
                });
            }
            else if (Object.keys(strokePropertyMap).includes(property)) {
                var transformedValue_1 = '';
                if (property === 'outlineDasharray') {
                    var paramValue_1 = fillSymbolizer[property];
                    transformedValue_1 = '';
                    paramValue_1.forEach(function (dash, idx) {
                        transformedValue_1 += dash;
                        if (idx < paramValue_1.length - 1) {
                            transformedValue_1 += ' ';
                        }
                    });
                }
                else if (property === 'outlineWidth') {
                    transformedValue_1 = fillSymbolizer[property] + '';
                }
                else {
                    transformedValue_1 = fillSymbolizer[property];
                }
                strokeCssParameters.push({
                    '_': transformedValue_1,
                    '$': {
                        'name': strokePropertyMap[property]
                    }
                });
            }
        });
        var polygonSymbolizer = [{}];
        if (fillCssParameters.length > 0 || graphicFill) {
            polygonSymbolizer[0].Fill = [{}];
            if (graphicFill) {
                polygonSymbolizer[0].Fill[0].GraphicFill = [graphicFill.PointSymbolizer[0]];
            }
            if (fillCssParameters.length > 0) {
                polygonSymbolizer[0].Fill[0].CssParameter = fillCssParameters;
            }
        }
        if (strokeCssParameters.length > 0) {
            polygonSymbolizer[0].Stroke = [{
                    'CssParameter': strokeCssParameters
                }];
        }
        return {
            'PolygonSymbolizer': polygonSymbolizer
        };
    };
    /**
     * Get the SLD Object (readable with xml2js) from an GeoStyler-Style LineSymbolizer.
     *
     * @param {LineSymbolizer} lineSymbolizer A GeoStyler-Style LineSymbolizer.
     * @return {object} The object representation of a SLD LineSymbolizer (readable with xml2js)
     */
    SldStyleParser.prototype.getSldLineSymbolizerFromLineSymbolizer = function (lineSymbolizer) {
        var propertyMap = {
            color: 'stroke',
            width: 'stroke-width',
            opacity: 'stroke-opacity',
            join: 'stroke-linejoin',
            cap: 'stroke-linecap',
            dasharray: 'stroke-dasharray',
            dashOffset: 'stroke-dashoffset'
        };
        var result = {
            'LineSymbolizer': [{
                    'Stroke': [{}]
                }]
        };
        var cssParameters = Object.keys(lineSymbolizer)
            .filter(function (property) { return property !== 'kind' && propertyMap[property]; })
            .map(function (property) {
            var value = lineSymbolizer[property];
            if (property === 'dasharray') {
                value = lineSymbolizer.dasharray.join(' ');
            }
            // simple transformation since geostyler-style uses prop 'miter' whereas sld uses 'mitre'
            if (property === 'join' && value === 'miter') {
                value = 'mitre';
            }
            return {
                '_': value,
                '$': {
                    'name': propertyMap[property]
                }
            };
        });
        var perpendicularOffset = lineSymbolizer.perpendicularOffset;
        if (_get(lineSymbolizer, 'graphicStroke')) {
            if (_get(lineSymbolizer, 'graphicStroke.kind') === 'Mark') {
                var graphicStroke = this.getSldPointSymbolizerFromMarkSymbolizer(lineSymbolizer.graphicStroke);
                result.LineSymbolizer[0].Stroke[0].GraphicStroke = [graphicStroke.PointSymbolizer[0]];
            }
            else if (_get(lineSymbolizer, 'graphicStroke.kind') === 'Icon') {
                var graphicStroke = this.getSldPointSymbolizerFromIconSymbolizer(lineSymbolizer.graphicStroke);
                result.LineSymbolizer[0].Stroke[0].GraphicStroke = [graphicStroke.PointSymbolizer[0]];
            }
        }
        if (_get(lineSymbolizer, 'graphicFill')) {
            if (_get(lineSymbolizer, 'graphicFill.kind') === 'Mark') {
                var graphicFill = this.getSldPointSymbolizerFromMarkSymbolizer(lineSymbolizer.graphicFill);
                result.LineSymbolizer[0].Stroke[0].GraphicFill = [graphicFill.PointSymbolizer[0]];
            }
            else if (_get(lineSymbolizer, 'graphicFill.kind') === 'Icon') {
                var graphicFill = this.getSldPointSymbolizerFromIconSymbolizer(lineSymbolizer.graphicFill);
                result.LineSymbolizer[0].Stroke[0].GraphicFill = [graphicFill.PointSymbolizer[0]];
            }
        }
        if (cssParameters.length !== 0) {
            result.LineSymbolizer[0].Stroke[0].CssParameter = cssParameters;
        }
        if (perpendicularOffset) {
            result.LineSymbolizer[0].PerpendicularOffset = [perpendicularOffset];
        }
        return result;
    };
    /**
     * Get the SLD Object (readable with xml2js) from an GeoStyler-Style MarkSymbolizer.
     *
     * @param {MarkSymbolizer} markSymbolizer A GeoStyler-Style MarkSymbolizer.
     * @return {object} The object representation of a SLD PointSymbolizer with a
     * Mark (readable with xml2js)
     */
    SldStyleParser.prototype.getSldPointSymbolizerFromMarkSymbolizer = function (markSymbolizer) {
        var mark = [{
                'WellKnownName': [
                    markSymbolizer.wellKnownName.toLowerCase()
                ]
            }];
        if (markSymbolizer.color) {
            mark[0].Fill = [{
                    'CssParameter': [{
                            '_': markSymbolizer.color,
                            '$': {
                                'name': 'fill'
                            }
                        }]
                }];
        }
        if (markSymbolizer.strokeColor || markSymbolizer.strokeWidth || markSymbolizer.strokeOpacity) {
            mark[0].Stroke = [{}];
            var strokeCssParameters = [];
            if (markSymbolizer.strokeColor) {
                strokeCssParameters.push({
                    '_': markSymbolizer.strokeColor,
                    '$': {
                        'name': 'stroke'
                    }
                });
            }
            if (markSymbolizer.strokeWidth) {
                strokeCssParameters.push({
                    '_': markSymbolizer.strokeWidth.toString(),
                    '$': {
                        'name': 'stroke-width'
                    }
                });
            }
            if (markSymbolizer.strokeOpacity) {
                strokeCssParameters.push({
                    '_': markSymbolizer.strokeOpacity.toString(),
                    '$': {
                        'name': 'stroke-opacity'
                    }
                });
            }
            mark[0].Stroke[0].CssParameter = strokeCssParameters;
        }
        var graphic = [{
                'Mark': mark
            }];
        if (markSymbolizer.opacity) {
            graphic[0].Opacity = [markSymbolizer.opacity.toString()];
        }
        if (markSymbolizer.radius) {
            graphic[0].Size = [(markSymbolizer.radius * 2).toString()];
        }
        if (markSymbolizer.rotate) {
            graphic[0].Rotation = [markSymbolizer.rotate.toString()];
        }
        return {
            'PointSymbolizer': [{
                    'Graphic': graphic
                }]
        };
    };
    /**
     * Get the SLD Object (readable with xml2js) from an GeoStyler-Style IconSymbolizer.
     *
     * @param {IconSymbolizer} iconSymbolizer A GeoStyler-Style IconSymbolizer.
     * @return {object} The object representation of a SLD PointSymbolizer with
     * en "ExternalGraphic" (readable with xml2js)
     */
    SldStyleParser.prototype.getSldPointSymbolizerFromIconSymbolizer = function (iconSymbolizer) {
        var onlineResource = [{
                '$': {
                    'xlink:type': 'simple',
                    'xmlns:xlink': 'http://www.w3.org/1999/xlink',
                    'xlink:href': iconSymbolizer.image
                }
            }];
        var graphic = [{
                'ExternalGraphic': [{
                        'OnlineResource': onlineResource
                    }]
            }];
        if (iconSymbolizer.image) {
            var iconExt = iconSymbolizer.image.split('.').pop();
            switch (iconExt) {
                case 'png':
                case 'jpeg':
                case 'gif':
                    graphic[0].ExternalGraphic[0].Format = ["image/" + iconExt];
                    break;
                case 'jpg':
                    graphic[0].ExternalGraphic[0].Format = ['image/jpeg'];
                    break;
                case 'svg':
                    graphic[0].ExternalGraphic[0].Format = ['image/svg+xml'];
                    break;
                default:
                    break;
            }
        }
        if (iconSymbolizer.opacity) {
            graphic[0].Opacity = iconSymbolizer.opacity;
        }
        if (iconSymbolizer.size) {
            graphic[0].Size = iconSymbolizer.size;
        }
        if (iconSymbolizer.rotate) {
            graphic[0].Rotation = iconSymbolizer.rotate;
        }
        return {
            'PointSymbolizer': [{
                    'Graphic': graphic
                }]
        };
    };
    /**
     * Get the SLD Object (readable with xml2js) from an GeoStyler-Style ComparisonFilter.
     *
     * @param {ComparisonFilter} comparisonFilter A GeoStyler-Style ComparisonFilter.
     * @return {object} The object representation of a SLD Filter Expression with a
     * comparison operator (readable with xml2js)
     */
    SldStyleParser.prototype.getSldComparisonFilterFromComparisonFilter = function (comparisonFilter) {
        var sldComparisonFilter = {};
        var operator = comparisonFilter[0];
        var key = comparisonFilter[1];
        var value = comparisonFilter[2];
        var sldOperators = SldStyleParser.keysByValue(SldStyleParser.comparisonMap, operator);
        var sldOperator = (sldOperators.length > 1 && value === null)
            ? sldOperators[1] : sldOperators[0];
        if (sldOperator === 'PropertyIsNull') {
            // empty, selfclosing Literals are not valid in a propertyIsNull filter
            sldComparisonFilter[sldOperator] = [{
                    'PropertyName': [key]
                }];
        }
        else if (sldOperator === 'PropertyIsLike') {
            sldComparisonFilter[sldOperator] = [{
                    '$': {
                        'wildCard': '*',
                        'singleChar': '.',
                        'escape': '!'
                    },
                    'PropertyName': [key],
                    'Literal': [value]
                }];
        }
        else {
            sldComparisonFilter[sldOperator] = [{
                    'PropertyName': [key],
                    'Literal': [value]
                }];
        }
        return sldComparisonFilter;
    };
    /**
     * Get the SLD Object (readable with xml2js) from an GeoStyler-Style Filter.
     *
     * @param {Filter} filter A GeoStyler-Style Filter.
     * @return {object} The object representation of a SLD Filter Expression (readable with xml2js)
     */
    SldStyleParser.prototype.getSldFilterFromFilter = function (filter) {
        var _this = this;
        var sldFilter = {};
        var _a = filter, operator = _a[0], args = _a.slice(1);
        if (Object.values(SldStyleParser.comparisonMap).includes(operator)) {
            sldFilter = this.getSldComparisonFilterFromComparisonFilter(filter);
        }
        else if (Object.values(SldStyleParser.combinationMap).includes(operator)) {
            var sldOperators = SldStyleParser.keysByValue(SldStyleParser.combinationMap, operator);
            // TODO Implement logic for "PropertyIsBetween" filter
            var combinator_1 = sldOperators[0];
            sldFilter[combinator_1] = [{}];
            args.forEach(function (subFilter, subFilterIdx) {
                var sldSubFilter = _this.getSldFilterFromFilter(subFilter);
                var filterName = Object.keys(sldSubFilter)[0];
                var isCombinationFilter = function (fName) { return ['And', 'Or'].includes(fName); };
                if (subFilter[0] === '||' || subFilter[0] === '&&') {
                    if (isCombinationFilter(filterName)) {
                        if (!(sldFilter[combinator_1][0][filterName])) {
                            sldFilter[combinator_1][0][filterName] = [];
                        }
                        sldFilter[combinator_1][0][filterName][subFilterIdx] = {};
                    }
                    else {
                        sldFilter[combinator_1][0][filterName] = {};
                    }
                    var parentFilterName_1 = Object.keys(sldSubFilter)[0];
                    subFilter.forEach(function (el, index) {
                        if (index > 0) {
                            var sldSubFilter2 = _this.getSldFilterFromFilter(el);
                            var filterName2 = Object.keys(sldSubFilter2)[0];
                            if (!(sldFilter[combinator_1][0][parentFilterName_1][subFilterIdx])) {
                                sldFilter[combinator_1][0][parentFilterName_1][subFilterIdx] = {};
                            }
                            if (!sldFilter[combinator_1][0][parentFilterName_1][subFilterIdx][filterName2]) {
                                sldFilter[combinator_1][0][parentFilterName_1][subFilterIdx][filterName2] = [];
                            }
                            sldFilter[combinator_1][0][parentFilterName_1][subFilterIdx][filterName2]
                                .push(sldSubFilter2[filterName2][0]);
                        }
                    });
                }
                else {
                    if (Array.isArray(sldFilter[combinator_1][0][filterName])) {
                        sldFilter[combinator_1][0][filterName].push(sldSubFilter[filterName][0]);
                    }
                    else {
                        sldFilter[combinator_1][0][filterName] = sldSubFilter[filterName];
                    }
                }
            });
        }
        else if (Object.values(SldStyleParser.negationOperatorMap).includes(operator)) {
            sldFilter.Not = args.map(function (subFilter) { return _this.getSldFilterFromFilter(subFilter); });
        }
        return sldFilter;
    };
    /**
     * The name of the SLD Style Parser.
     */
    SldStyleParser.title = 'SLD Style Parser';
    SldStyleParser.negationOperatorMap = {
        Not: '!'
    };
    SldStyleParser.combinationMap = {
        And: '&&',
        Or: '||',
        PropertyIsBetween: '&&'
    };
    SldStyleParser.comparisonMap = {
        PropertyIsEqualTo: '==',
        PropertyIsNotEqualTo: '!=',
        PropertyIsLike: '*=',
        PropertyIsLessThan: '<',
        PropertyIsLessThanOrEqualTo: '<=',
        PropertyIsGreaterThan: '>',
        PropertyIsGreaterThanOrEqualTo: '>=',
        PropertyIsNull: '=='
    };
    return SldStyleParser;
}());
exports.SldStyleParser = SldStyleParser;
exports.default = SldStyleParser;
//# sourceMappingURL=SldStyleParser.js.map


//////////////////
// WEBPACK FOOTER
// ./node_modules/geostyler-sld-parser/build/dist/SldStyleParser.js
// module id = 144
// module chunks = 0